import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Award, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FindCoachPage() {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const { theme } = useTheme();
  const [coaches, setCoaches] = useState([]);
  const [myCoaches, setMyCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    // Check if Pro tier
    if (user?.subscription_tier === 'free') {
      toast.info('Upgrade to Pro to connect with coaches');
      setTimeout(() => {
        navigate('/pricing');
      }, 2000);
      return;
    }
    
    fetchCoaches();
    fetchMyCoaches();
  }, [user]);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API}/coaches/browse`, {
        timeout: 5000
      });
      setCoaches(response.data.coaches || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load coaches');
      setCoaches([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCoaches = async () => {
    try {
      const response = await axios.get(`${API}/player/my-coaches`, {
        headers: getAuthHeader()
      });
      setMyCoaches(response.data.coaches || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const connectWithCoach = async (coachId) => {
    setConnecting(coachId);
    try {
      const response = await axios.post(
        `${API}/player/connect-with-coach?coach_id=${coachId}`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.status === 'already_connected') {
        toast.info('Already connected with this coach');
      } else {
        toast.success('Connection request sent to coach!');
      }
      
      fetchMyCoaches();
    } catch (error) {
      toast.error('Failed to send request');
    } finally {
      setConnecting(null);
    }
  };

  const isConnected = (coachId) => {
    return myCoaches.some(c => c.id === coachId);
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/player-dashboard')} className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Find a Coach</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Connect with verified coaches to improve your game</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner"></div></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coaches.map((coach) => (
              <Card key={coach.id} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-xl font-bold text-white">
                      {coach.name?.charAt(0) || 'C'}
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{coach.name}</h3>
                      <div className="flex items-center space-x-1 text-sm">
                        <Award className="w-3 h-3 text-yellow-400" />
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{coach.rating || 0} rating</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} line-clamp-3`}>
                    {coach.bio || 'Experienced cricket coach'}
                  </p>
                  
                  {coach.specialization && coach.specialization.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {coach.specialization.map((spec, idx) => (
                        <span key={idx} className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs">
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>
                      {coach.experience_years || 0} years exp
                    </span>
                    <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>
                      {coach.total_students || 0} students
                    </span>
                  </div>
                  
                  {isConnected(coach.id) ? (
                    <Button disabled className="w-full" variant="outline">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      Connected
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => connectWithCoach(coach.id)}
                      disabled={connecting === coach.id}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      {connecting === coach.id ? 'Sending...' : 'Connect'}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
        
        {!loading && coaches.length === 0 && (
          <div className="text-center py-16">
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>No coaches available yet</p>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>Check back soon!</p>
          </div>
        )}
      </div>
    </div>
  );
}
